import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useTrashedEmployees,
  useRestoreEmployees,
  usePermanentlyDeleteEmployees,
} from '../../services/httpServices/queries';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { AlertDialog } from '../../components/ui/AlertDialog';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  RotateCcw,
  Trash2,
  ArrowLeft,
  Users,
} from 'lucide-react';

const LIMIT = 20;

function getInitials(firstName?: string, lastName?: string) {
  const f = firstName?.charAt(0) || '';
  const l = lastName?.charAt(0) || '';
  return (f + l).toUpperCase() || '?';
}

export function EmployeesTrash() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; employee?: any } | null>(null);
  const [blockedDialog, setBlockedDialog] = useState<{ title: string; messages: string[] } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useTrashedEmployees(page, LIMIT, {
    search: debouncedSearch.trim() || undefined,
  });
  const restoreEmployees = useRestoreEmployees();
  const permanentDelete = usePermanentlyDeleteEmployees();

  const employees = data?.data || [];
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch]);

  const employeesName = (employee: any) =>
    `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || employee?.name || 'Unknown';

  const allSelected = employees.length > 0 && employees.every((e: any) => selectedIds.includes(e.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !employees.some((e: any) => e.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...employees.map((e: any) => e.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleRestore = async (id: string) => {
    try {
      const res: any = await restoreEmployees.mutateAsync([id]);
      const blocked = res?.data?.blocked ?? [];
      if (blocked.length > 0) {
        toast.error(blocked[0]?.reason || 'This employee could not be restored');
        return;
      }
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      toast.success('Employee restored');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore employee');
    }
  };

  const handleBulkRestore = async () => {
    try {
      const res: any = await restoreEmployees.mutateAsync(selectedIds);
      const restored = res?.data?.restored?.length ?? 0;
      const blocked = res?.data?.blocked ?? [];
      if (restored > 0) toast.success(`Restored ${restored} employee${restored === 1 ? '' : 's'}`);
      if (blocked.length > 0) {
        toast.error(`${blocked.length} employee(s) could not be restored: ${blocked[0]?.reason ?? ''}`);
      } else if (restored === 0) {
        toast.error('No employees were restored');
      }
      setSelectedIds([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to restore employees');
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        const res: any = await permanentDelete.mutateAsync([confirmTarget.employee.id]);
        const blocked = res?.data?.blocked ?? [];
        if (blocked.length > 0) {
          setBlockedDialog({
            title: 'Cannot delete permanently',
            messages: [
              `${employeesName(confirmTarget.employee)} — ${blocked[0]?.reason ?? 'This employee could not be permanently deleted.'}`,
            ],
          });
          return;
        }
        setSelectedIds((prev) => prev.filter((x) => x !== confirmTarget.employee.id));
        toast.success('Employee permanently deleted');
      } else {
        const res: any = await permanentDelete.mutateAsync(selectedIds);
        const deleted = res?.data?.deleted?.length ?? 0;
        const blocked = res?.data?.blocked ?? [];
        if (deleted > 0) toast.success(`Permanently deleted ${deleted} employee${deleted === 1 ? '' : 's'}`);
        if (blocked.length > 0) {
          const nameById = new Map<string, string>(employees.map((e: any) => [e.id, employeesName(e)]));
          setBlockedDialog({
            title: `Cannot permanently delete ${blocked.length} employee${blocked.length === 1 ? '' : 's'}`,
            messages: blocked.map((b: any) => `${nameById.get(b.id) ?? b.id} — ${b.reason}`),
          });
        } else if (deleted === 0) {
          setBlockedDialog({
            title: 'No employees were deleted',
            messages: ['None of the selected employees could be permanently deleted.'],
          });
        }
        setSelectedIds([]);
      }
    } catch (err: any) {
      setBlockedDialog({
        title: 'Cannot delete permanently',
        messages: [err?.response?.data?.message || 'The employee(s) could not be permanently deleted.'],
      });
    } finally {
      setConfirmTarget(null);
    }
  };

  const busy = restoreEmployees.isPending || permanentDelete.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/employees')}
          className="p-2 rounded-lg hover:bg-background text-text-muted transition-colors cursor-pointer"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger/10 rounded-lg">
            <Trash2 className="w-5 h-5 text-danger" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-text">Employees Trash</h1>
            <p className="text-xs text-text-muted">Restore employees or delete them permanently</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search trashed employees..."
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            />
          </div>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRestore}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {restoreEmployees.isPending ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              Restore
            </button>
            <button
              onClick={() => setConfirmTarget({ mode: 'bulk' })}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {permanentDelete.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete permanently
            </button>
            <button
              onClick={() => setSelectedIds([])}
              disabled={busy}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load trashed employees</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={employees.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Employee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Position</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Trashed</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {employees.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-16 text-center">
                      <Users className="w-10 h-10 mx-auto mb-3 text-text-muted/40" />
                      <p className="text-sm font-medium text-text-muted mb-1">
                        {search ? 'No trashed employees match your search' : 'Trash is empty'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  employees.map((employee: any) => {
                    const name = employeesName(employee);
                    return (
                      <tr key={employee.id} className="hover:bg-background/50 transition-colors">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(employee.id)}
                            onChange={() => toggleSelectOne(employee.id)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                              {getInitials(employee.firstName, employee.lastName)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-text truncate" title={name}>{name}</div>
                              <span className="text-xs text-text-muted font-mono">{employee.employeeCode || '—'}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text">{employee.designation?.name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-text-muted">{employee.department?.name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-text-muted">
                          {employee.deletedAt ? new Date(employee.deletedAt).toLocaleString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleRestore(employee.id)}
                              disabled={busy}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                              title="Restore"
                            >
                              <RotateCcw size={16} />
                            </button>
                            <button
                              onClick={() => setConfirmTarget({ mode: 'single', employee })}
                              disabled={busy}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                              title="Delete permanently"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="trashed employees"
            />
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Delete employees permanently' : 'Delete employee permanently'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Permanently delete ${selectedIds.length} selected employee(s)? This cannot be undone. Employees still linked to other records cannot be deleted.`
          ) : (
            <>
              Permanently delete <strong>{confirmTarget?.employee ? employeesName(confirmTarget.employee) : ''}</strong>?
              This cannot be undone. Employees still linked to other records cannot be deleted.
            </>
          )
        }
        confirmLabel="Delete permanently"
        loading={busy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      <AlertDialog
        open={blockedDialog !== null}
        title={blockedDialog?.title ?? 'Cannot delete permanently'}
        messages={blockedDialog?.messages}
        onClose={() => setBlockedDialog(null)}
      />
    </div>
  );
}
